import { viewerStrings } from '../copy/strings.js';
import { message, hasText } from '../copy/text.js';
import { useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import type { RecordsState } from '../state/records-state.js';
import type { WorkerClient } from '../runtime/worker-client.js';
import type { UnifiedRecordsState } from '../state/unified-records-state.js';
import { exportRelationships } from '../presentation/export-relationships.js';
import { relationshipExportState } from '../state/relationship-export-state.js';

/** Mount the selected spreadsheet only when its view is requested. */
export function SpreadsheetView({
  records,
  client,
  unified,
}: {
  records: RecordsState;
  client: WorkerClient;
  unified: UnifiedRecordsState;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    let dispose: (() => void) | undefined;
    setLoading(true);
    setError('');
    void import('../spreadsheet/index.js')
      .then(({ mountSpreadsheet }) => {
        if (!active) return;
        dispose = mountSpreadsheet(host.current!, {
          records,
          client,
          chartsHref: '#charts',
          unified,
          exportUnified: async (mode, selected) => {
            const snapshot = unified.getSnapshot();
            const fields = unified.fields.getSnapshot();
            const { query, columns } = relationshipExportState(unified, selected);
            const result = await exportRelationships(
              (query) => client.request({ operation: 'relationshipQuery', query }),
              {
                query,
                fields: columns,
                mode,
                timezone: fields.preference.timezone,
                cancelled: () => !active,
                current: () =>
                  unified.getSnapshot().generation === snapshot.generation &&
                  unified.fields.getSnapshot() === fields,
                progress: () => {},
              },
            );
            return {
              csv: await result.blob.text(),
              rows: result.rows,
              protectedCells: result.protectedCells,
            };
          },
        });
        setLoading(false);
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(
            message(viewerStrings.webComponentsSpreadsheetView.spreadsheetCouldNotOpen, {
              value1: String(reason),
            }),
          );
          setLoading(false);
        }
      });
    return () => {
      active = false;
      dispose?.();
    };
  }, [records, client, unified]);
  return (
    <section className="spreadsheet-surface">
      {loading &&
        (hasText(viewerStrings.webComponentsSpreadsheetView.loadingSpreadsheet) ? (
          <p role="status">{viewerStrings.webComponentsSpreadsheetView.loadingSpreadsheet}</p>
        ) : null)}
      {hasText(error) && <Alert type="error">{error}</Alert>}
      <div ref={host} />
    </section>
  );
}
